import React from 'react';
import { Modal, Text, View } from 'react-native';
import { radius, shadows, spacing, type, useAppTheme } from '@/theme';
import { Button } from './Button';
import { ScaledSheet } from '@/theme/responsive';

interface Props {
  visible: boolean;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Centered modal for anything that needs an explicit yes/no before acting — cancel booking,
 *  delete account, logout, etc. Never use a native Alert for these; keep the visual language
 *  consistent with the rest of the app. */
export function ConfirmDialog({ visible, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive, onConfirm, onCancel }: Props) {
  const { colors } = useAppTheme();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={[styles.overlay, { backgroundColor: colors.overlay }]}>
        <View style={[styles.card, { backgroundColor: colors.surface, borderColor: colors.border, borderWidth: 1 }]}>
          <Text style={[type.h3, { color: colors.textPrimary }]}>{title}</Text>
          {message ? <Text style={[type.body, styles.message, { color: colors.textSecondary }]}>{message}</Text> : null}
          <View style={styles.actions}>
            <View style={styles.actionItem}>
              <Button label={cancelLabel} variant="outlined" onPress={onCancel} />
            </View>
            <View style={styles.actionItem}>
              <Button label={confirmLabel} variant={destructive ? 'danger' : 'primary'} onPress={onConfirm} />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = ScaledSheet.create({
  overlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xxl },
  card: { width: '100%', maxWidth: 400, borderRadius: radius.lg, padding: spacing.xl, gap: spacing.sm, ...shadows.floating },
  message: { marginBottom: spacing.sm },
  actions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.sm },
  actionItem: { flex: 1 },
});
